import styled, { css } from 'styled-components';

type Props = {
  selectedDuration: number | null;
  setSelectedDuration: (duration: number | null) => void;
  /**
   * `sm` shrinks the chips for the Creator Studio rail, where six of them at the
   * default size eat a lot of a 350px panel. Defaults to `md` so Content Studio's
   * clip producer modal is unaffected.
   */
  size?: 'md' | 'sm';
};

const AVAILABLE_DURATION_OPTIONS = [7, 15, 30, 60, 90, 120];

const ClipDurationButtonList = (props: Props) => {
  const { selectedDuration, setSelectedDuration, size = 'md' } = props;
  const onFilterButtonClick = (option: number) => {
    if (option === selectedDuration) {
      setSelectedDuration(null);
    } else {
      setSelectedDuration(option);
    }
  };
  return (
    <DurationFilterButtons>
      {AVAILABLE_DURATION_OPTIONS.map(option => (
        <FilterButton
          key={option}
          $size={size}
          active={selectedDuration === option}
          onClick={() => onFilterButtonClick(option)}
          data-testid={`duration-${option}`}
        >
          {option}
        </FilterButton>
      ))}
    </DurationFilterButtons>
  );
};

export default ClipDurationButtonList;

const FilterButton = styled.div.attrs(
  (props: { active: boolean; $size: 'md' | 'sm' }) => props,
)`
  font-size: var(--text-xs);
  display: flex;
  border: 1px solid var(--border);
  background-color: var(--surface);
  color: var(--foreground);
  cursor: pointer;
  align-items: center;
  box-sizing: border-box;
  justify-content: center;
  transition:
    background-color var(--transition-fast) ease,
    border-color var(--transition-fast) ease;

  /* The doc's duration-button spec pins the colours but not the size. The sm
     variant is a deliberate sub-scale chip: six of these sit in one row, so the
     32px floor of the button scale would dominate a 350px rail. Medium weight
     rather than semi-bold for the same reason. */
  ${props =>
    props.$size === 'sm'
      ? css`
          height: 28px;
          min-width: 32px;
          padding: 0 6px;
          border-radius: var(--radius-input);
          font-weight: var(--font-weight-medium);
        `
      : css`
          padding: 8px;
          width: 38px;
          border-radius: 8px;
          font-weight: var(--font-weight-semi-bold);
        `}

  ${props =>
    props.active
      ? css`
          background-color: var(--accent);
          border-color: var(--accent);
          color: var(--background);
        `
      : css`
          &:hover {
            background-color: var(--surface-elevated);
          }
        `}
`;

const DurationFilterButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  width: fit-content;
  justify-content: center;
`;
